import { computedFrom } from "aurelia-binding";
import { autoinject, transient } from "aurelia-dependency-injection";
import { bindable } from "aurelia-templating";
import { Claims } from "../../../../../../../constants/authentication/claims";
import { RuleDisplayResultHelper } from "../../../../../../../helpers/rule-display-result-helper";
import { DynamicColumnModel, EntityDynamicValue } from "../../../../../../../models/basics/basics.models";
import { GetCapResponse, GetListItemResponse } from "../../../../../../../models/lists/list.models";
import { DataType } from "../../../../../../../models/queries/queries.models";
import { RuleDisplayResultSection } from "../../../../../../../models/rules/rules.models";
import { UserClaimsService } from "../../../../../../../services/authentication/user-claims.service";
import { CustomerService } from "../../../../../../../services/modules/customer-client.service";
import { VettingService } from "../../../../../../../services/vetting/vetting.service";
import { type PreArrivalRiskAnalysisRuleModel } from "../../../models/ports-and-terminals.models";

@transient()
@autoinject()
export class RiskAnalysisRuleAdditionalInfo
{
    listData: GetListItemResponse[];
    cap: GetCapResponse;
    sire: boolean = false;
    displaySections: RuleDisplayResultSection[];
    isLoading: boolean = false;
    isRightShipUser: boolean;

    @bindable
    task: PreArrivalRiskAnalysisRuleModel;

    @bindable
    vetId: number;

    @computedFrom("cap", "sire", "displaySections", "displaySections.length", "listData", "listData.length")
    get hasData(): boolean
    {
        return this.cap != null || this.sire || (this.displaySections && this.displaySections.length > 0) || (this.listData && this.listData.length > 0);
    }

    constructor(
        private customerService: CustomerService,
        private userClaimService: UserClaimsService,
        private vettingService: VettingService) 
    {
        this.isRightShipUser = this.userClaimService.hasPermission(Claims.RightShipUser);
    }

    getAdditionalDetails() 
    {
        return new Promise<void>((resolve, reject) => {
            if (this.task.hasListItems && !this.task.hasRsListItems) {
                this.vettingService.getListItems(this.task.vetTaskId).then((listItems) => {
                    this.listData = listItems;
                    resolve();
                }, e => {
                    this.isLoading = false;
                    reject(e)
                });
            }
            else if (this.task.hasCap) {
    
                this.vettingService.getCapResponse(this.vetId, this.task.vetTaskId).then(cap => {
                    this.cap = cap;
                    resolve();
                }, e => {
                    this.isLoading = false;
                    reject(e)
                });
            }
            else if (this.task.canViewDisplayResultsInOverview) {
                this.customerService.getDisplayResultJson(this.task.vetTaskId).then(displayResultJson => {
                    const displaySections: RuleDisplayResultSection[] = displayResultJson ? JSON.parse(displayResultJson).sections : [];
    
                    displaySections.forEach(section => {
                        section.items.forEach(item => {
                            item.text = RuleDisplayResultHelper.beatifyResultHtml(item, this.isRightShipUser);
                        });
                    });
    
                    this.displaySections = displaySections;
                    this.sire = this.displaySections.filter(x => x.items.filter(y => y.uri?.toLowerCase() == "vettingrulesires").length > 0).length > 0;
                    resolve();
                }, e => {
                    this.isLoading = false;
                    reject(e)
                });
            }
        });
    }

    async bind() 
    {
        if(this.hasData) return; //data alrready loaded skip get details

        this.isLoading = true;
        try{
            await this.getAdditionalDetails();
        }
        finally
        {
            this.isLoading = false;
        }
    }

    displayName(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.displayName;
    }

    isTextBox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Default ||
            column.tableColumnDataTypeId == DataType.Number ||
            column.tableColumnDataTypeId == DataType.Double;
    }

    isCheckbox(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.Boolean;
    }

    isDate(dynamicColumns: DynamicColumnModel[], entityDynamicValue: EntityDynamicValue) {
        const column = dynamicColumns.filter(x => x.columnId == entityDynamicValue.columnId)[0];
        return column.tableColumnDataTypeId == DataType.DateTime;
    }
}